import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";

import { UIButton, UIModal, UISelect } from "../ui";
import messages from "../../assets/locale/messages";
import { useSelector } from "react-redux";
import { clx } from "../../utils/Helpers";

import "./Note.scss";

const Note = ({
  location,
  otherValue,
  handleSubmit,
  selectValue,
  setSelectValue,
  setOtherValue,
  handleDelete,
  validate,
  setValidate,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared } = messages[lang];
  const [isModalDelete, setIsModalDelete] = useState(false);
  const [deleteID, setDeleteID] = useState(null);
  const required = 20;

  const locationOptions = useMemo(
    () => [
      ...(location ?? []).map((item) => ({ ...item })),
      { id: "__other__", name: shared.other, isOther: true },
    ],
    [location, shared.other],
  );

  const selectedLocationOption = useMemo(() => {
    if (!selectValue || selectValue === shared.selectedLocation) {
      return null;
    }
    if (selectValue === shared.other) {
      return locationOptions.find((option) => option.isOther) ?? null;
    }
    return (
      locationOptions.find((option) => option.name === selectValue) ?? null
    );
  }, [locationOptions, selectValue, shared.other, shared.selectedLocation]);

  const handleOpenDelete = (id) => {
    setIsModalDelete(true);
    setDeleteID(id);
  };

  const handleValidate = (e) => {
    const value = e.target.value;
    setOtherValue(value);
    setValidate(value.length >= required);
  };

  const handleLocationChange = (option) => {
    if (!option) {
      setSelectValue(shared.selectedLocation);
      return;
    }
    setSelectValue(option.isOther ? shared.other : option.name);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="note-form">
        <UISelect
          className="note-form__location-select"
          label={shared.location}
          name="location"
          placeholder={shared.selectedLocation}
          options={locationOptions}
          value={selectedLocationOption}
          disableClearable
          onChange={handleLocationChange}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option?.name || ""
          }
          isOptionEqualToValue={(option, selected) =>
            String(option?.id) === String(selected?.id)
          }
          renderOption={(optionProps, option) => {
            const { className, ...liProps } = optionProps;
            return (
            <Box
              component="li"
              {...liProps}
              key={option.id}
              className={clx("note-form__location-option", className)}
            >
              <span className="note-form__location-option-label">
                {option.name}
              </span>
              {!option.isOther && option.name !== selectValue ? (
                <button
                  type="button"
                  className={clx(
                    "note-form__location-delete",
                    lang === "en" ? "note-form__location-delete--end" : "note-form__location-delete--start",
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenDelete(option.id);
                  }}
                >
                  x
                </button>
              ) : null}
            </Box>
            );
          }}
        />

        {selectValue === shared.other && (
          <div className="note-form__other">
            <label htmlFor="note-other-input">{shared.other}</label>
            <input
              type="text"
              id="note-other-input"
              value={otherValue}
              onChange={handleValidate}
              placeholder={shared.locationPlaceholderOther}
            />
            {validate ? (
              <span className="note-form__error">{shared.locationError}</span>
            ) : null}
          </div>
        )}
      </form>

      <UIModal
        open={isModalDelete}
        onClose={() => setIsModalDelete(false)}
        maxWidth="xs"
        showClose={false}
        actions={
          <Stack direction="row" spacing={2} width="100%" className="note-form__delete-actions">
            <UIButton
              variant="outlined"
              color="inherit"
              fullWidth
              onClick={() => setIsModalDelete(false)}
            >
              {shared.cancel}
            </UIButton>
            <UIButton
              appearance="gradient"
              fullWidth
              onClick={() => {
                handleDelete(deleteID);
                setIsModalDelete(false);
              }}
            >
              {shared.confirm}
            </UIButton>
          </Stack>
        }
      >
        <Typography className="note-form__delete-message" align="center">
          {shared.sureMessage}
        </Typography>
      </UIModal>
    </>
  );
};

Note.propTypes = {
  location: PropTypes.array,
  otherValue: PropTypes.string,
  handleSubmit: PropTypes.func,
  selectValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectValue: PropTypes.func,
  setOtherValue: PropTypes.func,
  handleDelete: PropTypes.func,
  validate: PropTypes.bool,
  setValidate: PropTypes.func,
};

export default Note;
